import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../utils/BaseUrl';
import './MobileAppUpdates.css';

// Separate from user-session interceptors: this page uses its own management key.
const api = axios.create({ baseURL: API_BASE_URL, timeout: 15000 });
const storageKey = `mms.appUpdates.key:${API_BASE_URL}`;

function readKey() {
  const url = new URL(window.location.href);
  const incoming = url.searchParams.get('key');
  if (url.searchParams.has('key')) {
    url.searchParams.delete('key');
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    try { sessionStorage.setItem(storageKey, incoming || ''); } catch (_) { /* Memory-only if storage is blocked. */ }
    return incoming || '';
  }
  try { return sessionStorage.getItem(storageKey) || ''; } catch (_) { return ''; }
}

function PlatformSettings({ item, onSave, saving }) {
  const [version, setVersion] = useState(item.versionName);
  const [mandatory, setMandatory] = useState(item.isMandatory);
  const changed = version.trim() !== item.versionName || mandatory !== item.isMandatory;
  const platform = item.type === 'android' ? 'Android' : 'iOS';
  return <form className="app-updates__platform" onSubmit={(event) => {
    event.preventDefault();
    if (window.confirm(`Save ${platform} version ${version.trim()} as ${mandatory ? 'Mandatory' : 'Optional'}?${mandatory ? '\nOlder versions will be asked to update. Confirm this release is available in the store.' : ''}`)) {
      onSave(item.type, { versionName: version.trim(), isMandatory: mandatory });
    }
  }}>
    <div className="app-updates__platform-title"><h2>{platform}</h2><span>{item.versionName ? `Current: ${item.versionName}` : 'Not configured'}</span></div>
    <div className="app-updates__fields">
      <div><label htmlFor={`${item.type}-version`}>Version</label>
        <input id={`${item.type}-version`} className="form-control" value={version} onChange={(e) => setVersion(e.target.value)} required maxLength={32} pattern="(0|[1-9][0-9]*)\.(0|[1-9][0-9]*)\.(0|[1-9][0-9]*)" placeholder="1.0.76" disabled={saving} /></div>
      <div><label htmlFor={`${item.type}-policy`}>Update policy</label>
        <select id={`${item.type}-policy`} className="form-select" value={mandatory ? 'mandatory' : 'optional'} onChange={(e) => setMandatory(e.target.value === 'mandatory')} disabled={saving}>
          <option value="optional">Optional</option><option value="mandatory">Mandatory</option>
        </select></div>
      <button className="btn btn-primary" type="submit" disabled={saving || !changed || !version.trim()}>{saving ? 'Saving...' : 'Save changes'}</button>
    </div>
  </form>;
}

export default function MobileAppUpdates() {
  const [key, setKey] = useState(readKey);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [retry, setRetry] = useState(0);
  const lock = () => {
    try { sessionStorage.removeItem(storageKey); } catch (_) { /* Storage may be disabled. */ }
    setKey(''); setItems([]); setSuccess('');
  };

  useEffect(() => {
    if (!key) return;
    const controller = new AbortController();
    setLoading(true); setError('');
    api.get('/app-updates', { headers: { 'X-App-Update-Key': key }, signal: controller.signal })
      .then(({ data }) => setItems(data.data))
      .catch((err) => {
        if (controller.signal.aborted) return;
        if (err.response?.status === 403) lock();
        setError(err.response?.data?.message || 'Unable to load settings. Try again.');
      }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [key, retry]);

  const save = async (type, values) => {
    setSaving(type); setError(''); setSuccess('');
    try {
      const { data } = await api.put(`/app-updates/${type}`, values, { headers: { 'X-App-Update-Key': key } });
      setItems((previous) => previous.map((item) => item.type === type ? data.data : item));
      setSuccess(`${type === 'android' ? 'Android' : 'iOS'} update settings saved.`);
    } catch (err) {
      if (err.response?.status === 403) lock();
      setError(err.response?.data?.message || 'Unable to save settings. Try again.');
    } finally { setSaving(''); }
  };

  return <div className="app-updates">
    <header className="app-updates__header"><div>
      <img src="/Group.png" alt="Manage My Site" width="100" />
      {key && <button className="btn btn-outline-light" onClick={lock} disabled={Boolean(saving)}>Lock page</button>}
    </div></header>
    <main className="app-updates__main">
      <h1>Mobile App Updates</h1>
      <p className="app-updates__intro">Manage the configured release version and update policy for each platform.</p>
      {error && <div className="alert alert-danger" role="alert">{error}</div>}
      {success && <div className="alert alert-success" role="status">{success}</div>}
      {!key ? <section className="app-updates__notice"><h2>Management key required</h2><p>Open this page using your private link with a valid key. Normal user login does not grant access.</p></section>
        : loading ? <p role="status">Loading app versions...</p>
        : <>
          {items.map((item) => <PlatformSettings key={`${item.type}:${item.versionName}:${item.isMandatory}`} item={item} onSave={save} saving={Boolean(saving)} />)}
          {error && <button className="btn btn-outline-primary" disabled={Boolean(saving)} onClick={() => setRetry((value) => value + 1)}>Reload settings</button>}
          <p className="app-updates__hint">Publish the release in the app store before making its update mandatory. These settings show the configured version, not users' installed versions.</p>
        </>}
    </main>
  </div>;
}
